import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Gauge, Layers, Eye, EyeOff, TrendingUp, TrendingDown, Minus, CheckCircle2 } from "lucide-react";
import RecordTestForm from "@/components/RecordTestForm";
import PerformanceDashboard from "@/components/PerformanceDashboard";
import ProtocolInfo from "@/components/ProtocolInfo";

// Phase 1C — clinician-facing universal assessment panel.
// Assign reusable batteries, record side-aware measurements against existing
// test definitions (source = clinician_manual), control patient release, and
// read back the longitudinal computation (baseline→current, symmetry/LSI,
// clinician-defined target progress) from the existing results endpoint.
function sideOptions(mode) {
  if (mode === "bilateral") return [["bilateral", "Bilateral"]];
  if (mode === "unilateral") return [["left", "Left"], ["right", "Right"]];
  return [["bilateral", "Bilateral"], ["left", "Left"], ["right", "Right"]];
}

const Dir = ({ improved }) =>
  improved === true ? <TrendingUp size={14} className="text-[#22C55E]" />
  : improved === false ? <TrendingDown size={14} className="text-red-400" />
  : <Minus size={14} className="text-zinc-500" />;

// Server-stored derived metrics (take-off velocity, RSI, relative strength,
// peak force N). Displayed read-only from the saved reading — never recomputed
// in the frontend, so there's a single source of truth.
const DERIVED = [
  ["take_off_velocity_ms", "Take-off", "m/s"],
  ["rsi_ms", "RSI", "m/s"],
  ["relative_strength", "Rel. strength", "×BM"],
  ["peak_force_n", "Peak force", "N"],
];
const DerivedCell = ({ computed }) => {
  const items = DERIVED.filter(([k]) => computed && computed[k] != null);
  if (!items.length) return <div className="flex justify-end"><span className="text-zinc-600 text-xs">—</span></div>;
  return (
    <div className="flex flex-wrap gap-1 justify-end">
      {items.map(([k, label, unit]) => (
        <span key={k} className="text-[11px] bg-white/5 rounded px-1.5 py-0.5 text-zinc-300 whitespace-nowrap" data-testid={`pt-derived-${k}`}>
          {label} {computed[k]} {unit}
        </span>
      ))}
    </div>
  );
};

function ComputedCard({ item, def }) {
  const sides = item.sides || {};
  return (
    <div className="fl-card p-4" data-testid={`pt-computed-${item.test_definition_id.slice(0, 8)}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="fl-heading text-base flex items-center gap-1.5">
            {item.name}
            {def && <ProtocolInfo def={def} slug={`computed-${item.test_definition_id.slice(0, 8)}`} />}
          </div>
          <div className="text-xs text-zinc-400 mt-0.5">{item.category_label} · {item.unit}</div>
        </div>
        {item.symmetry && (
          <div className="text-right" data-testid={`pt-lsi-${item.test_definition_id.slice(0, 8)}`}>
            <div className="text-[11px] text-zinc-500">Symmetry (LSI)</div>
            <div className="text-lg fl-heading text-[#f47a20]">{item.symmetry.symmetry_index_pct}%</div>
          </div>
        )}
      </div>

      <div className="mt-3 grid gap-2">
        {Object.entries(sides).map(([side, s]) => (
          <div key={side} className="text-sm bg-white/5 rounded px-3 py-2 flex items-center justify-between gap-2">
            <span className="capitalize text-zinc-300 w-20">{side}</span>
            <span className="text-zinc-400 text-xs flex-1">
              {s.baseline.value} <span className="text-zinc-600">({s.baseline.date})</span>
              {" → "}
              <span className="text-white">{s.current.value}</span> <span className="text-zinc-600">({s.current.date})</span>
            </span>
            <span className="flex items-center gap-1 text-xs">
              <Dir improved={s.improved} />
              {s.count > 1 && s.pct_change != null ? `${s.pct_change > 0 ? "+" : ""}${s.pct_change}%` : "—"}
            </span>
          </div>
        ))}
      </div>

      {item.target && (
        <div className="mt-2 text-xs flex items-center gap-2" data-testid={`pt-target-${item.test_definition_id.slice(0, 8)}`}>
          <span className="text-zinc-500">Target {item.target.comparator === "gte" ? "≥" : "≤"} {item.target.target_value}:</span>
          {item.target.met
            ? <span className="text-[#22C55E] flex items-center gap-1"><CheckCircle2 size={12} /> met</span>
            : <span className="text-amber-400">{item.target.pct_to_target != null ? `${item.target.pct_to_target}% there` : "in progress"}</span>}
        </div>
      )}
      {item.symmetry?.note && <div className="text-[11px] text-zinc-500 mt-2">{item.symmetry.note}</div>}
    </div>
  );
}

export default function UniversalPhysicalTestsPanel({ patientId }) {
  const [defs, setDefs] = useState([]);
  const [batteries, setBatteries] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [computed, setComputed] = useState([]);
  const [raw, setRaw] = useState([]);
  const [legacy, setLegacy] = useState([]);
  const [showAssign, setShowAssign] = useState(false);
  const [showRecord, setShowRecord] = useState(false);
  const [busy, setBusy] = useState(false);

  const [assignForm, setAssignForm] = useState({ battery_id: "", context: "rehabilitation", notes: "" });
  const [recForm, setRecForm] = useState({
    test_definition_id: "", battery_assignment_id: "", side: "bilateral", value: "",
    performed_at: new Date().toISOString().slice(0, 10), condition: "", equipment: "", notes: "", released: false,
  });

  const loadCatalog = () => {
    api.get("/physical-tests/definitions").then((r) => setDefs(r.data || [])).catch(() => {});
    api.get("/physical-tests/batteries").then((r) => setBatteries(r.data || [])).catch(() => {});
  };
  const loadPatient = () => {
    api.get(`/physical-tests/assignments/patient/${patientId}`).then((r) => setAssignments(r.data || [])).catch(() => {});
    api.get(`/physical-tests/results/patient/${patientId}`).then((r) => {
      setComputed(r.data?.computed || []);
      setRaw(r.data?.raw || []);
      setLegacy(r.data?.legacy || []);
    }).catch(() => {});
  };
  useEffect(() => { loadCatalog(); }, []);
  useEffect(() => { if (patientId) loadPatient(); }, [patientId]);

  const defsById = useMemo(() => Object.fromEntries((defs || []).map((d) => [d.id, d])), [defs]);
  const selectedDef = useMemo(() => defs.find((d) => d.id === recForm.test_definition_id), [defs, recForm.test_definition_id]);
  const availSides = sideOptions(selectedDef?.sided_mode);

  const chooseDef = (id) => {
    const d = defs.find((x) => x.id === id);
    const opts = sideOptions(d?.sided_mode);
    setRecForm((f) => ({ ...f, test_definition_id: id, side: opts[0][0] }));
  };

  const assign = async () => {
    if (!assignForm.battery_id) { toast.error("Choose a battery."); return; }
    setBusy(true);
    try {
      await api.post("/physical-tests/assignments", { ...assignForm, patient_id: patientId, notes: assignForm.notes || null });
      toast.success("Battery assigned to patient.");
      setShowAssign(false); setAssignForm({ battery_id: "", context: "rehabilitation", notes: "" });
      loadPatient();
    } catch (e) { toast.error(e?.response?.data?.detail || "Assign failed"); }
    finally { setBusy(false); }
  };

  const record = async () => {
    if (!recForm.test_definition_id) { toast.error("Choose a test definition."); return; }
    if (recForm.value === "" || recForm.value == null) { toast.error("Enter a measured value."); return; }
    setBusy(true);
    try {
      await api.post("/physical-tests/results", {
        test_definition_id: recForm.test_definition_id,
        patient_id: patientId,
        battery_assignment_id: recForm.battery_assignment_id || null,
        side: recForm.side,
        value: parseFloat(recForm.value),
        performed_at: new Date(recForm.performed_at).toISOString(),
        condition: recForm.condition || null,
        equipment: recForm.equipment || null,
        notes: recForm.notes || null,
        released: recForm.released,
        source: "clinician_manual",
      });
      toast.success("Measurement recorded.");
      setShowRecord(false);
      setRecForm((f) => ({ ...f, value: "", condition: "", notes: "", released: false }));
      loadPatient();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const toggleRelease = async (r) => {
    try {
      await api.patch(`/physical-tests/results/${r.id}/release`, { released: !r.released });
      toast.success(!r.released ? "Released to patient." : "Hidden from patient.");
      loadPatient();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const inp = "fl-input w-full text-sm px-2 py-2 rounded-md";
  const [view, setView] = useState("assess");

  const SubTabs = () => (
    <div className="flex gap-2 mb-4" data-testid="pt-subtabs">
      {[["assess", "Assessments"], ["perf", "F–V & Performance"]].map(([v, l]) => (
        <button key={v} type="button" onClick={() => setView(v)} data-testid={`pt-subtab-${v}`}
          className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${view === v ? "border-[#f47a20] text-[#f47a20] bg-[#f47a20]/10" : "border-white/10 text-zinc-400 hover:text-zinc-200"}`}>{l}</button>
      ))}
    </div>
  );

  if (view === "perf") {
    return (
      <div className="fl-card fl-card-elevated p-6 mt-4" data-testid="universal-physical-tests-panel">
        <SubTabs />
        <PerformanceDashboard patientId={patientId} computed={computed} />
      </div>
    );
  }

  return (
    <div className="fl-card fl-card-elevated p-6 mt-4" data-testid="universal-physical-tests-panel">
      <SubTabs />
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="fl-heading text-xl flex items-center gap-2"><Gauge size={18} /> Universal assessments <span className="fl-label ml-1">Phase 1C</span></div>
        <div className="flex gap-2">
          <button onClick={() => { setShowAssign((s) => !s); setShowRecord(false); }} className="fl-btn-ghost text-sm inline-flex items-center gap-1" data-testid="pt-assign-toggle"><Layers size={14} /> Assign battery</button>
          <button onClick={() => { setShowRecord((s) => !s); setShowAssign(false); }} className="fl-btn text-sm inline-flex items-center gap-1" data-testid="pt-record-toggle"><Plus size={14} /> Record result</button>
        </div>
      </div>

      {showAssign && (
        <div className="fl-card p-4 mb-4 border border-[#f47a20]/30" data-testid="pt-assign-form">
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <div className="fl-label mb-1">Battery *</div>
              <select className={inp} value={assignForm.battery_id} onChange={(e) => setAssignForm((f) => ({ ...f, battery_id: e.target.value }))} data-testid="pt-assign-battery">
                <option value="">Select…</option>
                {batteries.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <div className="fl-label mb-1">Context</div>
              <select className={inp} value={assignForm.context} onChange={(e) => setAssignForm((f) => ({ ...f, context: e.target.value }))} data-testid="pt-assign-context">
                {["rehabilitation", "performance", "screening", "return_to_sport", "post_op", "non_diagnostic"].map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
              </select>
            </div>
            <div>
              <div className="fl-label mb-1">Notes</div>
              <input className={inp} value={assignForm.notes} onChange={(e) => setAssignForm((f) => ({ ...f, notes: e.target.value }))} data-testid="pt-assign-notes" />
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <button onClick={assign} disabled={busy} className="fl-btn text-sm" data-testid="pt-assign-save">{busy ? "Assigning…" : "Assign"}</button>
            <button onClick={() => setShowAssign(false)} className="fl-btn-ghost text-sm">Cancel</button>
          </div>
        </div>
      )}

      {showRecord && (
        <RecordTestForm defs={defs} assignments={assignments} patientId={patientId}
          onSaved={() => { setShowRecord(false); loadPatient(); }}
          onCancel={() => setShowRecord(false)} />
      )}

      {/* Active assignments */}
      {assignments.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2" data-testid="pt-assignments-list">
          {assignments.map((a) => (
            <span key={a.id} className="text-[11px] bg-white/5 text-zinc-300 rounded-full px-3 py-1 border border-white/10">
              {a.battery_name} · {a.context} · {a.status}
            </span>
          ))}
        </div>
      )}

      {/* Longitudinal computation */}
      {computed.length > 0 ? (
        <div className="grid md:grid-cols-2 gap-3" data-testid="pt-computed-grid">
          {computed.map((item) => <ComputedCard key={item.test_definition_id} item={item} def={defsById[item.test_definition_id]} />)}
        </div>
      ) : (
        <div className="text-zinc-500 text-sm" data-testid="pt-computed-empty">No universal test results yet. Assign a battery and record the first measurement.</div>
      )}

      {/* Raw results with release control */}
      {raw.length > 0 && (
        <div className="mt-6 overflow-x-auto">
          <div className="fl-label mb-2">All recorded measurements (clinician view)</div>
          <table className="w-full text-sm" data-testid="pt-raw-table">
            <thead>
              <tr className="text-left fl-label border-b border-white/10">
                <th className="py-2">Date</th><th className="py-2">Test</th><th className="py-2">Side</th>
                <th className="py-2 text-right">Value</th><th className="py-2 pl-4">Source</th><th className="py-2 pl-4 text-right">Derived</th><th className="py-2 text-right">Patient</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {[...raw].sort((a, b) => String(b.performed_at || b.created_at || "").localeCompare(String(a.performed_at || a.created_at || ""))).map((r) => (
                <tr key={r.id} className="text-zinc-300" data-testid={`pt-raw-row-${r.id.slice(0, 8)}`}>
                  <td className="py-2">{(r.performed_at || r.created_at || "").slice(0, 10)}</td>
                  <td className="py-2">
                    <span className="inline-flex items-center gap-1.5">
                      {r.definition_name || r.label}
                      {defsById[r.test_definition_id] && <ProtocolInfo def={defsById[r.test_definition_id]} slug={`raw-${r.id.slice(0, 8)}`} />}
                    </span>
                  </td>
                  <td className="py-2 capitalize">{r.side}</td>
                  <td className="py-2 text-right font-mono">{r.value} {r.unit}</td>
                  <td className="py-2 pl-4 text-xs">{r.source === "device_import" ? "Device" : "Manual"}</td>
                  <td className="py-2 pl-4"><DerivedCell computed={r.computed} /></td>
                  <td className="py-2 text-right">
                    <button onClick={() => toggleRelease(r)} className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded ${r.released ? "text-[#22C55E]" : "text-zinc-400 hover:text-white"}`} data-testid={`pt-release-${r.id.slice(0, 8)}`}>
                      {r.released ? <><Eye size={12} /> Released</> : <><EyeOff size={12} /> Hidden</>}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Increment D — legacy quick readings surfaced read-only in the unified history */}
      {legacy.length > 0 && (
        <div className="mt-6 overflow-x-auto" data-testid="pt-legacy-history">
          <div className="fl-label mb-1">Earlier quick readings (legacy · read-only)</div>
          <div className="text-[11px] text-zinc-500 mb-2">Captured before the unified assessment system. Original values, units, dates and source are preserved — not converted, re-typed or folded into the trend comparisons above.</div>
          <table className="w-full text-sm" data-testid="pt-legacy-table">
            <thead>
              <tr className="text-left fl-label border-b border-white/10">
                <th className="py-2">Date</th><th className="py-2">Test</th><th className="py-2">Side</th>
                <th className="py-2 text-right">Value</th><th className="py-2 pl-4">Source</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {legacy.map((r) => (
                <tr key={r.id} className="text-zinc-300" data-testid={`pt-legacy-row-${(r.id || "").slice(0, 8)}`}>
                  <td className="py-2">{(r.performed_at || r.created_at || "").slice(0, 10)}</td>
                  <td className="py-2">{r.label || r.test_type || "—"}</td>
                  <td className="py-2 capitalize">{r.side || "—"}</td>
                  <td className="py-2 text-right font-mono">{r.value} {r.unit || r.units || ""}</td>
                  <td className="py-2 pl-4 text-xs">{r.source === "webhook" ? "Device webhook" : "Legacy manual"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
